import { Deck, SerializableDeck } from "../models/Deck";
import { DeckConfig } from "../models/DeckConfig";
import Modal from 'react-modal';
import React, { useCallback, useRef, useState } from "react";
import { useSwitch } from "./SwitchHook";
import { WindowSize } from "../models/WindowSize";
import SvgExportRender from "./SvgExportRender";
import { TextDeckWriter } from "../services/TextDeckWriter";
import { TextDeckReader } from "../services/TextDeckReader";
import { SerializableCard } from "../models/Card";

export default function BuilderHeader(props: { deck: Deck, onClearAllCards: () => any, windowSize: WindowSize, toScaleUp: () => any, toScaleDown: () => any, onDeckLoaded: (deck: SerializableDeck) => void }) {

    const { deck, onClearAllCards, onDeckLoaded } = props;
    const [isOpenConfirmClearModal, confirmClearModalOperation] = useSwitch(false);

    const clearAllCards = useCallback(() => {
        onClearAllCards();
        confirmClearModalOperation.off();
    }, [onClearAllCards, confirmClearModalOperation]);

    const [isOpenConfirmExportModal, confirmExportModalOperation] = useSwitch(false);

    const userHeaderValue = useRef<string>('OSICAデッキ');

    const [isRequestedExportSvg, setIsRequestedExportSvg] = useState(false);
    const handleOnSvgExported = useCallback(() => {
        //console.log("OnSvgExported");
        setIsRequestedExportSvg(false);
        confirmExportModalOperation.off();
    }, [confirmExportModalOperation]);

    const handleStartSvgExport = useCallback(() => {
        setIsRequestedExportSvg(true);
    }, []);

    const handleStartTextExport = useCallback(() => {
        (new TextDeckWriter(deck)).downloadTextDeck(userHeaderValue.current ?? 'OSICAデッキ');
        confirmExportModalOperation.off();
    }, [deck, confirmExportModalOperation]);

    const [errorMessage, setErrorMessage] = useState('');
    const [invalidErrorCards, setInvalidErrorCards] = useState<SerializableCard[]>([]);

    const handleCloseError = useCallback(() => {
        setErrorMessage('');
    }, []);

    const handleCloseInvalidErrorCards = useCallback(() => {
        setInvalidErrorCards([]);
    }, []);

    const [isOpenFileOpenConfirmModal, confirmFileOpenConfirmModalOperation] = useSwitch(false);

    const handleOpenFile = useCallback(async (file: File) => {
        try {
            confirmFileOpenConfirmModalOperation.off();
            const result = await TextDeckReader.openTextDeckAsync(file);
            setInvalidErrorCards(result.invalidCards);
            onDeckLoaded(result.deck);
        } catch (error) {
            setErrorMessage('ファイルを開けませんでした。データが正しいことを確認して、再度お試しください。');
        }
    }, [onDeckLoaded, confirmFileOpenConfirmModalOperation]);

    return <>
        <div className="builder-header">
            <div className="title-bar">
                <div className="window-operator">
                    <button type="button" className="btn px-1" disabled={props.windowSize === "large"} onClick={props.toScaleUp}>
                        <svg width="24" height="24" viewBox="0 0 24 24">
                            <polygon fill="currentColor" points="22.59 15.41 12 10.6 1.41 15.41 .59 13.59 12 8.4 23.41 13.59 22.59 15.41" />
                        </svg>
                    </button>
                    <button type="button" className="btn px-1" disabled={props.windowSize === "small"} onClick={props.toScaleDown}>
                        <svg width="24" height="24" viewBox="0 0 24 24">
                            <polygon fill="currentColor" points="1.41 8.5 12 13.31 22.59 8.5 23.41 10.32 12 15.5 .59 10.32 1.41 8.5" />
                        </svg>
                    </button>
                </div>
                <div className="title ps-2 flex-grow-1">
                    Deck Builder
                </div>
                <div className="small-value align-self-center">
                    {props.deck.totalCardCount}
                </div>
            </div>
            <div className="header-actions">
                <div className="me-3">
                    <span className="me-2">
                        カード枚数:
                    </span>
                    <span className="value">
                        {props.deck.totalCardCount} / {DeckConfig.DeckTotalCount}
                    </span>
                </div>
                <div>
                    <UploadButton onClick={confirmFileOpenConfirmModalOperation.on} />
                    <button type="button" className="btn btn-primary" onClick={confirmExportModalOperation.on} title="ダウンロード">
                        <svg viewBox="0 0 49.62 49.62">
                            <path className="fill-orange" d="M24.81,48.87c-13.27,0-24.06-10.8-24.06-24.06S11.54,.75,24.81,.75c13.27,0,24.06,10.8,24.06,24.06s-10.79,24.06-24.06,24.06h0Z" />
                            <path className="fill-white" d="M34.73,20.67h-5.67v-7.08c0-.77-.62-1.39-1.39-1.39h-5.71c-.77,0-1.39,.62-1.39,1.39v7.08h-5.67c-1.08,0-1.72,1.2-1.13,2.09l9.92,15.05c.53,.81,1.72,.81,2.26,0l9.92-15.05c.59-.9-.05-2.09-1.13-2.09Z" />
                            <path className="fill-white" d="M24.81,1.5c12.85,0,23.31,10.46,23.31,23.31s-10.46,23.31-23.31,23.31c-12.85,0-23.31-10.46-23.31-23.31C1.5,11.96,11.96,1.5,24.81,1.5h0m0-1.5C11.11,0,0,11.11,0,24.81c0,13.7,11.11,24.81,24.81,24.81h0c13.7,0,24.81-11.11,24.81-24.81S38.51,0,24.81,0h0Z" />
                        </svg>
                    </button>
                    <button type="button" className="btn btn-primary" onClick={confirmClearModalOperation.on} title="デッキの初期化">
                        <svg viewBox="0 0 49.62 49.62">
                            <g>
                                <path className="fill-orange" d="M24.81,48.87c-13.27,0-24.06-10.8-24.06-24.06S11.54,.75,24.81,.75c13.27,0,24.06,10.8,24.06,24.06s-10.79,24.06-24.06,24.06h0Z" />
                                <path className="fill-white" d="M24.81,1.5c12.85,0,23.31,10.46,23.31,23.31,0,12.85-10.46,23.31-23.31,23.31-12.85,0-23.31-10.46-23.31-23.31S11.96,1.5,24.81,1.5h0m0-1.5C11.11,0,0,11.11,0,24.81s11.11,24.81,24.81,24.81h0c13.7,0,24.81-11.11,24.81-24.81,0-13.7-11.11-24.81-24.81-24.81h0Z" />
                            </g>
                            <path className="fill-white" d="M17.21,30.22l-4.42,4.42c-.59,.59-.59,1.54,0,2.12,.29,.29,.68,.44,1.06,.44s.77-.15,1.06-.44l4.41-4.41c-.81-.59-1.53-1.31-2.11-2.13Z" />
                            <path className="fill-white" d="M36.82,12.73c-.59-.59-1.54-.59-2.12,0l-4.48,4.48c.82,.58,1.54,1.3,2.13,2.11l4.47-4.47c.59-.59,.59-1.54,0-2.12Z" />
                            <path className="fill-white" d="M24.81,15.48c.51,0,1.01,.05,1.5,.13v-6.35c0-.83-.67-1.5-1.5-1.5s-1.5,.67-1.5,1.5v6.35c.49-.08,.99-.13,1.5-.13Z" />
                            <path className="fill-white" d="M24.81,34.14c-.51,0-1.01-.05-1.5-.13v6.22c0,.83,.67,1.5,1.5,1.5s1.5-.67,1.5-1.5v-6.22c-.49,.08-.99,.13-1.5,.13Z" />
                            <path className="fill-white" d="M19.4,17.21l-4.48-4.48c-.59-.59-1.54-.59-2.12,0-.59,.59-.59,1.54,0,2.12l4.47,4.47c.59-.81,1.31-1.53,2.13-2.11Z" />
                            <path className="fill-white" d="M36.82,34.63l-4.42-4.42c-.58,.82-1.3,1.54-2.11,2.13l4.41,4.41c.29,.29,.68,.44,1.06,.44s.77-.15,1.06-.44c.59-.59,.59-1.54,0-2.12Z" />
                            <path className="fill-white" d="M15.48,24.81c0-.53,.06-1.06,.14-1.57h-6.3c-.83,0-1.5,.67-1.5,1.5s.67,1.5,1.5,1.5h6.28c-.07-.47-.12-.95-.12-1.43Z" />
                            <path className="fill-white" d="M40.3,23.24h-6.3c.09,.51,.14,1.03,.14,1.57,0,.49-.05,.96-.12,1.43h6.28c.83,0,1.5-.67,1.5-1.5s-.67-1.5-1.5-1.5Z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <SvgExportRender deck={props.deck} headerLine1Value={userHeaderValue} fileName={userHeaderValue} isRenderEnabled={isRequestedExportSvg} onExported={handleOnSvgExported} />
        <ConfirmClearModal isOpen={isOpenConfirmClearModal} onCancel={confirmClearModalOperation.off} onOk={clearAllCards} />
        <ConfirmOpenModal isOpen={isOpenFileOpenConfirmModal} onCancel={confirmFileOpenConfirmModalOperation.off} onOpenFile={handleOpenFile} />
        <ConfirmExportModal
            isOpen={isOpenConfirmExportModal}
            onCancel={confirmExportModalOperation.off}
            userHeaderValue={userHeaderValue}
            onSaveImage={handleStartSvgExport}
            onSaveText={handleStartTextExport}
            isProcessing={isRequestedExportSvg} />
        <ErrorMessageModal
            message={errorMessage}
            onClose={handleCloseError}
        />
        <InvalidCardErrorMessageModal cards={invalidErrorCards} onClose={handleCloseInvalidErrorCards} />
    </>
}

function ConfirmClearModal(props: { isOpen: boolean, onCancel: () => any, onOk: () => any }) {
    return <Modal
        isOpen={props.isOpen}
        onRequestClose={props.onCancel}
        contentLabel="Modal"
        className="modal-dialog modal-dialog-centered"
        overlayClassName="react-modal-overlay"
    >
        <div className="modal-content">
            <div className="modal-body">
                <p className="mb-0">登録されたカードをすべて削除してもよろしいですか？</p>
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary rounded-pill" onClick={props.onCancel}>
                    キャンセル
                </button>
                <button type="button" className="btn btn-danger rounded-pill" onClick={props.onOk}>
                    OK
                </button>
            </div>
        </div>
    </Modal>
}

function ConfirmExportModal(props: { isOpen: boolean, userHeaderValue: React.MutableRefObject<string>, onCancel: () => any, onSaveImage: () => any, onSaveText: () => any, isProcessing?: boolean }) {
    const { userHeaderValue } = props;

    const [descriptionText, setDescriptionText] = useState(userHeaderValue.current);

    const onInputDescription = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        userHeaderValue.current = event.target.value;
        setDescriptionText(userHeaderValue.current);
    }, [userHeaderValue]);


    return <Modal
        isOpen={props.isOpen}
        onRequestClose={props.onCancel}
        contentLabel="Modal"
        className="modal-dialog modal-dialog-centered"
        overlayClassName="react-modal-overlay"
    >
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title">デッキの保存</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={props.onCancel}></button>
            </div>
            <div className="modal-body">
                <div className="mb-3">
                    <label className="form-label">デッキの説明を入力（任意）</label>
                    <input type="text" className="form-control" placeholder="OSICAデッキ" maxLength={30} value={descriptionText} onChange={onInputDescription} />
                </div>
                <p className="mb-2">
                    <small>
                        <strong>ヒント: </strong>デッキを保存して後から再編集したい場合と、OSICA公式イベントでデッキを提出する場合は「テキストで保存」をしてください。<br />
                    </small>
                </p>
                <p className="mb-2">
                    <small>
                        <strong>免責：</strong>テキストで保存したデッキは、予告なく再編集できなくなる可能性がございます。予めご了承ください。
                    </small>
                </p>
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary rounded-pill" onClick={props.onCancel}>
                    キャンセル
                </button>
                <button type="button" className="btn btn-primary rounded-pill" onClick={props.onSaveText}>
                    {'テキストで保存'}
                </button>
                <button type="button" className="btn btn-primary rounded-pill" onClick={props.onSaveImage} disabled={props.isProcessing}>
                    {props.isProcessing ? '画像を生成しています...' : '画像で保存'}
                </button>
            </div>
        </div>
    </Modal>
}

function UploadButton(props: { onClick: () => any }) {
    return <>
        <button className="btn btn-primary" title="開く" onClick={props.onClick}>
            <svg viewBox="0 0 49.62 49.62">
                <path className="fill-orange" d="M24.81,48.87c-13.27,0-24.06-10.8-24.06-24.06S11.54,.75,24.81,.75c13.27,0,24.06,10.8,24.06,24.06s-10.79,24.06-24.06,24.06h0Z" />
                <path className="fill-white" d="M24.81,1.5c12.85,0,23.31,10.46,23.31,23.31s-10.46,23.31-23.31,23.31c-12.85,0-23.31-10.46-23.31-23.31S11.96,1.5,24.81,1.5h0m0-1.5C11.11,0,0,11.11,0,24.81s11.11,24.81,24.81,24.81h0c13.7,0,24.81-11.11,24.81-24.81S38.51,0,24.81,0h0Z" />
                <path className="fill-white" d="M14.89,29.95h5.67v7.08c0,.77,.62,1.39,1.39,1.39h5.71c.77,0,1.39-.62,1.39-1.39v-7.08h5.67c1.08,0,1.72-1.2,1.13-2.09l-9.92-15.05c-.53-.81-1.72-.81-2.26,0l-9.92,15.05c-.59,.9,.05,2.09,1.13,2.09Z" />
            </svg>
        </button>
    </>
}

function ErrorMessageModal(props: { message?: string, onClose: () => any }) {
    return <Modal
        isOpen={!!props.message}
        onRequestClose={props.onClose}
        contentLabel="Modal"
        className="modal-dialog modal-dialog-centered"
        overlayClassName="react-modal-overlay"
    >
        <div className="modal-content">
            <div className="modal-body">
                <p>
                    {props.message}
                </p>
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-primary rounded-pill" onClick={props.onClose}>
                    OK
                </button>
            </div>
        </div>
    </Modal>
}

function InvalidCardErrorMessageModal(props: { cards?: SerializableCard[], onClose: () => any }) {
    const isOverflow = props.cards ? props.cards.length >= 6 : false;
    const hasData = props.cards !== undefined && props.cards.length >= 1;
    return <Modal
        isOpen={hasData}
        onRequestClose={props.onClose}
        contentLabel="Modal"
        className="modal-dialog modal-dialog-centered"
        overlayClassName="react-modal-overlay"
    >
        <div className="modal-content">
            <div className="modal-body">
                <p>
                    データに含まれていた無効なカードを除外しました。<br />
                    読み込まれたデッキを確認して、構築しなおしてください。
                </p>
                <ul>
                    {
                        props.cards ? props.cards.slice(0, 5).map(card => {
                            return <li>{card.cardNo}</li>
                        }) : null
                    }
                    {
                        isOverflow ? <li>(最初の5件を表示しています。)</li> : null
                    }
                </ul>
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-primary rounded-pill" onClick={props.onClose}>
                    OK
                </button>
            </div>
        </div>
    </Modal>
}

function ConfirmOpenModal(props: { isOpen: boolean, onCancel: () => any, onOpenFile: (file: File) => any }) {

    const { onCancel, onOpenFile } = props;
    const uploadFileInput = useRef<HTMLInputElement>(null);

    const handleButtonClick = useCallback(() => {
        uploadFileInput.current?.click();
    }, [uploadFileInput]);

    const handleFileSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.currentTarget.files || event.currentTarget.files.length === 0) {
            onCancel();
            return;
        }
        const file = event.currentTarget.files[0];
        if (file) {
            onOpenFile(file);
        }
        event.currentTarget.value = "";
    }, [onCancel, onOpenFile]);

    return <Modal
        isOpen={props.isOpen}
        onRequestClose={props.onCancel}
        contentLabel="Modal"
        className="modal-dialog modal-dialog-centered"
        overlayClassName="react-modal-overlay"
    >
        <div className="modal-content">
            <div className="modal-body">
                <p className="mb-0">登録されたカードをすべて削除し、保存したデッキを読み込みますか？</p>
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary rounded-pill" onClick={props.onCancel}>
                    キャンセル
                </button>
                <button type="button" className="btn btn-primary rounded-pill" onClick={handleButtonClick}>
                    開く
                </button>
                <input type="file" style={{ display: "none" }} ref={uploadFileInput} onChange={handleFileSelected} accept="text/plain" />
            </div>
        </div>
    </Modal>
}
